import React, { useState, useMemo } from "react";
import { Sidebar } from "../components/Sidebar";
import { MarketplaceHeader } from "../components/marketplace/MarketplaceHeader";
import { CategoryFilter } from "../components/marketplace/CategoryFilter";
import { PartnerCard } from "../components/marketplace/PartnerCard";
import { AIPartnerIntelligence } from "../components/marketplace/AIPartnerIntelligence";
import { ReferralTracking } from "../components/marketplace/ReferralTracking";
import { MarketplaceInsightsPanel } from "../components/marketplace/MarketplaceInsightsPanel";
import { partners } from "../data/marketplaceData";
import { PremiumInput } from "../components/brand/PremiumInput";

export default function MarketplacePage() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("rating");

  const filtered = useMemo(() => {
    let list = [...partners];
    if (active !== "all") list = list.filter((p) => p.category === active);
    if (query) list = list.filter((p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.tagline.toLowerCase().includes(query.toLowerCase()) ||
      p.specialisms.some((s) => s.toLowerCase().includes(query.toLowerCase()))
    );
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "speed") list.sort((a, b) => a.responseHrs - b.responseHrs);
    if (sort === "conversion") list.sort((a, b) => b.conversionPct - a.conversionPct);
    return list;
  }, [active, query, sort]);

  return (
    <div className="min-h-screen gp-surface">
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out xl:mr-[360px] flex flex-col min-h-screen" data-testid="marketplace-main">
        <MarketplaceHeader />

        <div className="px-4 md:px-10 py-8 space-y-8">
          <AIPartnerIntelligence />

          <CategoryFilter active={active} onSelect={setActive} />

          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="cf-h-section mr-2">{filtered.length} partners</h2>
            <div className="flex items-center gap-1 bg-white rounded-lg p-1" style={{ border: "1px solid rgba(15,23,42,0.06)" }}>
              {[
                { id: "rating", label: "Rating" },
                { id: "speed", label: "Speed" },
                { id: "conversion", label: "Conversion" },
              ].map((s) => (
                <button
                  key={s.id}
                  data-testid={`sort-${s.id}`}
                  onClick={() => setSort(s.id)}
                  className={`h-8 px-3 rounded-md text-[12px] font-medium transition-all ${
                    sort === s.id ? "text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                  style={sort === s.id ? { background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)" } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="ml-auto w-[260px]">
              <PremiumInput
                value={query}
                onChange={setQuery}
                placeholder="Search partners, specialisms…"
                format="search"
                size="sm"
                clearable
                testid="mkt-search"
              />
            </div>
          </div>

          {/* Partner grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <PartnerCard key={p.id} partner={p} />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed bg-white p-12 text-center text-[13px]" style={{ borderColor: "rgba(15,23,42,0.10)", color: "#64748B" }}>
                No partners match the current filters.
              </div>
            )}
          </div>

          <ReferralTracking />
        </div>
      </main>
      <MarketplaceInsightsPanel />
    </div>
  );
}
